import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from './LoginForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { MFAForm } from './MFAForm';
import { PasswordResetForm } from './PasswordResetForm';
import { SSOLoginForm } from './SSOLoginForm';
import { ChangePasswordForm } from './ChangePasswordForm';
import { SignupForm } from './SignupForm';
import loginFormLogo from '../../assets/icons/loginFormLogo.svg';
import loginPage1 from '../../assets/images/loginPage1.svg';
import loginPage2 from '../../assets/images/loginPage2.svg';
import loginPage3 from '../../assets/images/loginPage3.svg';
import { Toast } from '../ui/Toast';

interface AuthProps {
  onAuthSuccess?: (user: any, token: string) => void;
  onAuthError?: (error: string) => void;
  className?: string;
  showRememberMe?: boolean;
}

export const Auth: React.FC<AuthProps> = ({
  onAuthSuccess,
  onAuthError,
  className = '',
  showRememberMe = true,
}) => {
  const { authStep, user, token, email, clearAuthState, goToForgotPassword, initializeAuth } = useAuth();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Carousel data
  const carouselSlides = [
    {
      text: 'Create and manage your consent journeys with ease',
      svg: loginPage1
    },
    {
      text: 'Advanced analytics and journey breakdowns for maximum context',
      svg: loginPage2
    },
    {
      text: 'Complete control over your data; decide who gets access to what',
      svg: loginPage3
    }
  ];

  const handleShowToast = useCallback((message: string) => {
    setToastMessage(message);
  }, []);

  const closeToast = useCallback(() => {
    setToastMessage(null);
  }, []);

  // Initialize auth state from sessionStorage on mount
  // This restores authStep to 'change-password' if user was redirected back from AuthGuard
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Debug logging
  useEffect(() => {
    console.log('React Auth component mounted');
    console.log('Auth state:', { authStep, user, token, email });
  }, []);

  useEffect(() => {
    console.log('Auth step changed:', authStep);
  }, [authStep]);

  // Handle successful authentication
  useEffect(() => {
    if (user && token) {
      onAuthSuccess?.(user, token);
    }
  }, [user, token, onAuthSuccess]);

  // Clear auth state on unmount
  useEffect(() => {
    return () => {
      clearAuthState();
    };
  }, [clearAuthState]);

  // Auto-rotate carousel every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 2000); // Change slide every 2 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [carouselSlides.length]);

  const renderAuthStep = () => {
    console.log('renderAuthStep called with authStep:', authStep);
    
    switch (authStep) {
      case 'login':
        console.log('Rendering LoginForm');
        return (
          <LoginForm
            onSuccess={onAuthSuccess}
            onError={onAuthError}
            onForgotPassword={goToForgotPassword}
            showRememberMe={showRememberMe}
            onShowToast={handleShowToast}
          />
        );

      case 'forgot-password':
        console.log('Rendering ForgotPasswordForm');
        return (
          <ForgotPasswordForm
            onShowToast={handleShowToast}
            onOTPSent={(emailAddress) => {
              // The email is now stored in Redux state
              console.log('OTP sent to:', emailAddress);
            }}
          />
        );

      case 'mfa':
        console.log('Rendering MFAForm');
        return (
          <MFAForm
            onSuccess={onAuthSuccess}
            email={email}
            onShowToast={handleShowToast}
          />
        );

      case 'reset-password':
        console.log('Rendering PasswordResetForm');
        return (
          <PasswordResetForm
            email={email}
            onSuccess={() => {
              // Password reset successful
              console.log('Password reset successful');
            }}
          />
        );

      case 'sso':
        console.log('Rendering SSOLoginForm');
        return (
          <SSOLoginForm
            onSuccess={onAuthSuccess}
            onError={onAuthError}
          />
        );

      case 'change-password':
        console.log('Rendering ChangePasswordForm');
        return (
          <ChangePasswordForm
            onSuccess={() => {
              console.log('Password changed successfully, clearing state and redirecting to login');
              handleShowToast('Password changed successfully. Please login with your new password.');
              // Auth step will be changed to 'login' by Redux reducer
            }}
            onShowToast={handleShowToast}
          />
        );

      case 'signup':
        return (
          <SignupForm
            onShowToast={handleShowToast}
          />
        );

      default:
        console.log('Rendering default LoginForm, authStep was:', authStep);
        return (
          <LoginForm
            onSuccess={onAuthSuccess}
            onError={onAuthError}
            onForgotPassword={goToForgotPassword}
            showRememberMe={showRememberMe}
            onShowToast={handleShowToast}
          />
        );
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div
        className={`h-screen bg-white relative overflow-hidden ${className}`}>
      <div className="w-full h-full flex">
        {/* Left Side - Logo/Brand Area */}
        <div className="w-1/2 h-full flex flex-col p-6" style={{ background: 'linear-gradient(to bottom, #FFFFFF, #F2F8FB)', border: '1px solid #C1E4FB' }}>
          <div className="flex flex-col h-full">
            <div className="text-left border-b border-[#C1E4FB]">
              <div className="px-4 pt-4 pb-2">
                <h1 className="text-[40px] font-medium leading-none tracking-[0.02em] flex items-center gap-4 text-[#0F3340]">
                  <span>👋🏼</span>
                  <span className='ml-2'>Welcome to FinPro</span>
                </h1>
              </div>

              {/* Three action words in one line */}
              <div className="px-4 pb-4">
                <div className="flex gap-4 text-[#0F3340]" style={{ marginTop: '1rem' }}>
                  <div className="text-[24px] font-normal leading-none tracking-[0.02em]">Aggregate.</div>
                  <div className="text-[24px] ml-2 font-normal leading-none tracking-[0.02em]">Assess.</div>
                  <div className="text-[24px] ml-2 font-normal leading-none tracking-[0.02em]">Act</div>
                </div>
              </div>
            </div>

            {/* Carousel Section - Center */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
              {/* Circular Container for SVG with smooth transitions */}
              <div
                className="w-56 h-56 rounded-full flex items-center justify-center relative"
                style={{
                  backgroundColor: '#E9F1FA',
                  boxShadow: '0 10px 40px rgba(15, 51, 64, 0.08), 0 2px 8px rgba(15, 51, 64, 0.04)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'scale(1)',
                }}
              >
                <img
                  key={currentSlide}
                  src={carouselSlides[currentSlide].svg}
                  alt={`Slide ${currentSlide + 1}`}
                  className="w-40 h-40 object-contain"
                  style={{
                    animation: 'fadeInScale 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </div>

              {/* Carousel Text with fade animation */}
              <p
                key={`text-${currentSlide}`}
                className="text-center max-w-sm mb-6"
                style={{
                  color: '#0F3340',
                  marginTop: '1.5rem',
                  fontFamily: 'Helvetica Neue, sans-serif',
                  fontWeight: 500,
                  fontSize: '1.25rem',
                  lineHeight: '1.4',
                  letterSpacing: '0.02em',
                  textAlign: 'center',
                  animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  minHeight: '3.5rem',
                }}
              >
                {carouselSlides[currentSlide].text}
              </p>

              {/* Pagination Dots with smooth transitions */}
              <div className="flex gap-2 items-center">
                {carouselSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className="rounded-full transition-all duration-300 ease-in-out"
                    style={{
                      width: currentSlide === index ? '32px' : '8px',
                      height: '8px',
                      backgroundColor: currentSlide === index ? '#0F3340' : '#919CAB',
                      border: 'none',
                      outline: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      transform: currentSlide === index ? 'scale(1)' : 'scale(0.9)',
                      opacity: currentSlide === index ? 1 : 0.6,
                    }}
                    onMouseEnter={(e) => {
                      if (currentSlide !== index) {
                        e.currentTarget.style.backgroundColor = '#7A8595';
                        e.currentTarget.style.opacity = '0.8';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentSlide !== index) {
                        e.currentTarget.style.backgroundColor = '#919CAB';
                        e.currentTarget.style.opacity = '0.6';
                        e.currentTarget.style.transform = 'scale(0.9)';
                      }
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Bottom section with toast and copyright */}
            <div className="w-full">
              {toastMessage && (
                <div className="w-full px-4 mb-4">
                  <Toast message={toastMessage} onClose={closeToast} />
                </div>
              )}

              {/* Copyright - always visible at bottom */}
              <div className="pb-8 px-4 mb-4 text-sm text-left" style={{ color: '#0F3340' }}>
                © 2025 Moneyone. All Rights Reserved.
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Auth Form Container */}
        <div className="w-1/2 flex flex-col p-6 bg-white">
          <div className="w-full max-w-2xl mx-auto flex flex-col h-full">
            <div className="">
              {/* Logo */}
              <div className="flex mb-2">
                <img 
                  src={loginFormLogo} 
                  alt="FinPro Logo" 
                  className="w-8 h-12"
                />
              </div>
              
              <h2 className="uppercase mb-1" style={{
                color: '#0F3340',
                fontFamily: 'Helvetica Neue, sans-serif',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                lineHeight: '100%',
                letterSpacing: '0.02em'
              }}>
                {authStep === 'forgot-password' ? 'RESET YOUR PASSWORD' :
                 authStep === 'mfa' ? 'Two-Factor Authentication' :
                 authStep === 'sso' ? 'Single Sign-On' :
                 authStep === 'change-password' ? 'CHANGE YOUR PASSWORD' :
                 authStep === 'signup' ? 'CREATE YOUR ACCOUNT' : 'LOGIN TO FINPRO'}
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                {authStep === 'mfa' ? '' :
                 authStep === 'sso' ? 'Sign in with your organization' : ''}
              </p>
            </div>
            
            <div className="flex-1 flex flex-col">
              {renderAuthStep()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      {/* <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-center text-xs text-gray-400">
        <p>
          © 2024 FinPro Admin. All rights reserved.
        </p>
        <div className="mt-2 space-x-4">
          <a href="#" className="hover:text-gray-600 transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-gray-600 transition-colors">Terms of Service</a>
          <a href="#" className="hover:text-gray-600 transition-colors">Support</a>
        </div>
      </div> */}
      </div>
    </>
  );
};
